import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import ModuleNav from "@/components/ModuleNav";
import ErrorBoundary from "@/components/ErrorBoundary";

// Pages
import { LoginPage } from "@/pages/LoginPage";
import { RegisterPage } from "@/pages/RegisterPage";
import { TenantDashboard } from "@/pages/tenant/TenantDashboard";
import { ReportIssuePage } from "@/pages/tenant/ReportIssuePage";
import { IssueChatPage } from "@/pages/tenant/IssueChatPage";
import { JobTrackingPage } from "@/pages/tenant/JobTrackingPage";
import { ContractorDashboard } from "@/pages/contractor/ContractorDashboard";
import { ContractorAvailability } from "@/pages/contractor/ContractorAvailability";
import { ContractorOnboardPage } from "@/pages/contractor/ContractorOnboardPage";
import { ContractorInvoicesPage } from "@/pages/contractor/ContractorInvoicesPage";
import { ContractorMessagesPage } from "@/pages/contractor/ContractorMessagesPage";
import { ContractorSettingsPage } from "@/pages/contractor/ContractorSettingsPage";
import ContractorJobDetailPage from "@/pages/contractor/ContractorJobDetailPage";
import { ContractorDetailsPage } from "@/pages/admin/ContractorDetailsPage";
import { AdminReportsPage } from "@/pages/admin/AdminReportsPage";
import ContractAcceptPage from "@/pages/contract/ContractAcceptPage";
import { LandlordDashboard } from "@/pages/landlord/LandlordDashboard";
import { AdminDashboard } from "@/pages/admin/AdminDashboard";
import { PropertiesPage } from "@/pages/admin/PropertiesPage";
import { InspectionsPage } from "@/pages/admin/InspectionsPage";
import { CompliancePage } from "@/pages/admin/CompliancePage";
import { PaymentTrackingPage } from "@/pages/admin/PaymentTrackingPage";
import { SyncPage } from "@/pages/admin/SyncPage";
import { LettingsPage } from "@/pages/admin/LettingsPage";
import { LettingsDashboard } from "@/pages/lettings/LettingsDashboard";
import { RentManagementPage } from "@/pages/admin/RentManagementPage";
import { LandlordDetailPage } from "@/pages/admin/LandlordDetailPage";
import { TenantDetailPage } from "@/pages/admin/TenantDetailPage";
import { PropertyDetailPage } from "@/pages/admin/PropertyDetailPage";
import { BulkImportPage } from "@/pages/admin/BulkImportPage";
import { BulkContactsUpdate } from "@/pages/admin/BulkContactsUpdate";
import { JobDetailPage } from "@/pages/JobDetailPage";
import { DocumentSignPage } from "@/pages/DocumentSignPage";
import { AuthCallback } from "@/pages/AuthCallback";
import { RoleSwitcher } from "@/components/RoleSwitcher";
import { SalesDashboard } from "@/pages/sales/SalesDashboard";
import SalesPageSimple from "@/pages/sales/SalesPageSimple";
import SimpleFlowsPage from "@/pages/sales/SimpleFlowsPage";
import NewInstructionPage from "@/pages/sales/NewInstructionPage";
import SimpleNewInstructionPage from "@/pages/SimpleNewInstructionPage";
import ClientDashboardPage from "@/pages/ClientDashboardPage";
import { SalesLandingPage } from "@/pages/sales/SalesLandingPage";
import SalesHomeDashboard from "@/pages/sales/SalesHomeDashboard";
import { CaseHubPage } from "@/pages/sales/CaseHubPage";
import PropertyDashboard from "@/pages/sales/PropertyDashboard";
import { SalesImportPage } from "@/pages/sales/SalesImportPage";
import { SalesAnalyticsPage } from "@/pages/sales/SalesAnalyticsPage";
import { ApplicantDetailsPage } from "@/pages/sales/ApplicantDetailsPage";
import VendorDetailPage from "@/pages/sales/VendorDetailPage";
import BuyerDetailPage from "@/pages/sales/BuyerDetailPage";
import { CaseDocumentsPage } from "@/pages/sales/CaseDocumentsPage";
import { SalesParticularsPage } from "@/pages/sales/SalesParticularsPage";
import { PropertyBrochurePage } from "@/pages/sales/PropertyBrochurePage";
import { CaseOffersPage } from "@/pages/sales/CaseOffersPage";
import { CaseViewingsPage } from "@/pages/sales/CaseViewingsPage";
import { InstructionsListPage } from "@/pages/sales/InstructionsListPage";
import InstructionDetailPage from "@/pages/sales/InstructionDetailPage";
import InstructionHubPage from "@/pages/sales/InstructionHubPage";
import { ListingDetailPage } from "@/pages/sales/ListingDetailPage";
import { MarketingDetailsPage } from "@/pages/sales/MarketingDetailsPage";
import { PortalExportsPage } from "@/pages/marketing/PortalExportsPage";
import { PropertyMarketingPage } from "@/pages/marketing/PropertyMarketingPage";
import { SellerBrochureApprovalPage } from "@/pages/seller/SellerBrochureApprovalPage";
import SellerContractPage from "@/pages/seller/SellerContractPage";
import ContractsManagementPage from "@/pages/sales/ContractsManagementPage";
import { AccountsDashboard } from "@/pages/accounts/AccountsDashboard";
import { XeroSyncPage } from "@/pages/accounts/XeroSyncPage";
import { XeroCallbackPage } from "@/pages/accounts/XeroCallbackPage";
import { LandlordServicesPage } from "@/pages/accounts/LandlordServicesPage";
import { AccountsPage as AccountsInvoicingPage } from "@/pages/admin/AccountsPage";
import { InventoryDashboard } from "@/pages/inventory/InventoryDashboard";
import { BookInspectionPage } from "@/pages/BookInspectionPage";
import { LettingsAnalyticsPage } from "@/pages/admin/LettingsAnalyticsPage";
import { MaintenanceAnalyticsPage } from "@/pages/admin/MaintenanceAnalyticsPage";
import { AMLVerificationPage } from "@/pages/admin/AMLVerificationPage";
import { MyDocumentsPage } from "@/pages/client/MyDocumentsPage";
import { StaffProfile } from "@/pages/StaffProfile";
import SolicitorDashboard from "@/pages/solicitor/SolicitorDashboard";
import SolicitorTemplatesPage from "@/pages/solicitor/SolicitorTemplatesPage";
import LandlordDemoPage from "@/pages/LandlordDemoPage";
import InventoryReportsPage from "@/pages/inventory/InventoryReportsPage";
import ReportEditorPage from "@/pages/inventory/ReportEditorPage";
import TenantReportViewPage from "@/pages/inventory/TenantReportViewPage";
import LandlordReportViewPage from "@/pages/inventory/LandlordReportViewPage";
import ReportComparisonPage from "@/pages/inventory/ReportComparisonPage";
import DiaryPage from "@/pages/DiaryPage";
import ContactsPage from "@/pages/ContactsPage";
import ContactDetailPage from "@/pages/ContactDetailPage";
import ListingNewPage from "@/pages/listings/ListingNewPage";
import NewListingDetailPage from "@/pages/listings/ListingDetailPage";
import SalesProgressionPage from "@/pages/progression/SalesProgressionPage";
import TenancyProgressionPage from "@/pages/progression/TenancyProgressionPage";

// Client Portal Pages
import PortalLogin from "@/pages/portal/PortalLogin";
import PortalRegister from "@/pages/portal/PortalRegister";
import SellerDashboard from "@/pages/portal/SellerDashboard";
import SellerProgressionPage from "@/pages/portal/SellerProgressionPage";
import SellerParticularsReview from "@/pages/portal/SellerParticularsReview";
import BuyerDashboardPortal from "@/pages/portal/BuyerDashboard";
import SolicitorPortalLogin from "@/pages/portal/SolicitorPortalLogin";
import SolicitorPortalDashboard from "@/pages/portal/SolicitorPortalDashboard";
import PortalLandlordDashboard from "@/pages/portal/LandlordDashboard";
import PortalTenantDashboard from "@/pages/portal/TenantDashboard";

// Buyer Portal Pages (Self-registration)
import ApplicantRegister from "@/pages/buyer/ApplicantRegister";
import BuyerLogin from "@/pages/buyer/BuyerLogin";
import BuyerDashboard from "@/pages/buyer/BuyerDashboard";
import PropertySearch from "@/pages/buyer/PropertySearch";
import BuyerOnboarding from "@/pages/buyer/BuyerOnboarding";
import BuyerViewings from "@/pages/buyer/BuyerViewings";

// Home Dashboard
import HomeDashboard from "@/pages/HomeDashboard";
import ModuleLauncher from "@/pages/ModuleLauncher";

// Maintenance Module
import MaintenanceDashboard from "@/pages/maintenance/MaintenanceDashboard";

// Staff & Commission
import CommissionDashboard from "@/pages/CommissionDashboard";
import StaffManagement from "@/pages/StaffManagement";

// Sales Progressor Dashboard
import SalesProgressorDashboard from "@/pages/sales/SalesProgressorDashboard";
import SolicitorFirmsPage from "@/pages/sales/SolicitorFirmsPage";
import SolicitorFirmDashboard from "@/pages/sales/SolicitorFirmDashboard";
import SolicitorAnalyticsPage from "@/pages/sales/SolicitorAnalyticsPage";
import SalesPropertyPage from "@/pages/sales/SalesPropertyPage";
import SalesChainPage from "@/pages/sales/SalesChainPage";
import TraceyDashboard from "@/pages/sales/TraceyDashboard";
import ChainBuilderPage from "@/pages/sales/ChainBuilderPage";

// Sales Instructions Module (ISOLATED - DO NOT mix with progression)
import SIInstructionsListPage from "@/pages/sales-instructions/SIInstructionsListPage";
import SIPropertyOverviewPage from "@/pages/sales-instructions/SIPropertyOverviewPage";
import SellerInstructionPage from "@/pages/sales-instructions/SellerInstructionPage";
import PublishingJobsPage from "@/pages/admin/PublishingJobsPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth context
export const useAuth = () => {
  const [user, setUser] = useState(() => {
    const stored = localStorage.getItem("user");
    return stored ? JSON.parse(stored) : null;
  });
  const [token, setToken] = useState(() => localStorage.getItem("token"));

  const login = (userData, accessToken) => {
    setUser(userData);
    setToken(accessToken);
    localStorage.setItem("user", JSON.stringify(userData));
    localStorage.setItem("token", accessToken);
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem("user");
    localStorage.removeItem("token");
  };

  return { user, token, login, logout, isAuthenticated: !!token };
};

// Axios instance with auth and 401 handling
export const createAuthAxios = (token) => {
  const instance = axios.create({
    baseURL: API,
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });
  
  // Add response interceptor to handle expired tokens
  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        const requestUrl = error.config?.url || '';
        // Only clear staff token for staff API calls
        const isStaffApi = !requestUrl.includes('/portal/') && 
                           !requestUrl.includes('/solicitor-portal/') && 
                           !requestUrl.includes('/buyer-portal/');
        
        if (isStaffApi && localStorage.getItem('token')) {
          console.warn('Token expired, clearing session');
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          if (window.location.pathname !== '/login') {
            window.location.href = '/login?session=expired';
          }
        }
      }
      return Promise.reject(error);
    }
  );
  
  return instance;
};

// Staff roles that should have access to Sales and Lettings
const STAFF_ROLES = [
  "admin", "director", "sales_agent", "sales_negotiator", "sales_progressor",
  "lettings_manager", "lettings_negotiator", "accounts", "accounts_admin", "inventory_clerk"
];

// Check if JWT token is expired (client-side check)
const isTokenExpired = (token) => {
  if (!token) return true;
  try {
    // JWT tokens are base64 encoded, split by '.'
    const payload = JSON.parse(atob(token.split('.')[1]));
    // exp is in seconds, Date.now() is in milliseconds
    return payload.exp * 1000 < Date.now();
  } catch (e) {
    // If we can't parse the token, consider it expired
    return true;
  }
};

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles, allowedEmails }) => {
  const token = localStorage.getItem("token");
  const user = JSON.parse(localStorage.getItem("user") || "null");

  // Check if token exists and is not expired
  if (!token || !user || isTokenExpired(token)) {
    // Clear expired data
    if (token && isTokenExpired(token)) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      return <Navigate to="/login?session=expired" replace />;
    }
    return <Navigate to="/login" replace />;
  }

  // Check if user's email is in the allowed emails list
  const emailAllowed = allowedEmails && allowedEmails.includes(user.email);
  
  // Check if user's role is in the allowed roles list
  const roleAllowed = allowedRoles && allowedRoles.includes(user.role);

  if (!emailAllowed && !roleAllowed) {
    // Redirect to appropriate dashboard based on role
    const dashboardRoutes = {
      tenant: "/tenant",
      contractor: "/contractor",
      landlord: "/landlord",
      admin: "/home",
      accounts: "/home",
      accounts_admin: "/home",
      inventory_clerk: "/inventory",
      lettings_manager: "/home",
      sales_agent: "/home",
      director: "/home",
      sales_negotiator: "/home",
      lettings_negotiator: "/home",
      solicitor: `/sales/solicitor/${encodeURIComponent(user.firm_name || 'Unknown')}`
    };
    return <Navigate to={dashboardRoutes[user.role] || "/login"} replace />;
  }

  return children;
};

// Module Guard Component - redirects to /home if module is disabled
import { 
  MODULES, 
  getModuleFromPath, 
  isModuleEnabled 
} from '@/config/modules';

const ModuleGuard = ({ children, module }) => {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user") || "null");
  
  useEffect(() => {
    if (!isModuleEnabled(module, user)) {
      navigate('/home', { replace: true });
    }
  }, [module, user, navigate]);
  
  // If module disabled, return null (redirect will happen)
  if (!isModuleEnabled(module, user)) {
    return null;
  }
  
  return children;
};

// App Router
function AppRouter() {
  const location = useLocation();
  
  // Check for OAuth callback with session_id in hash
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />

      {/* Tenant routes */}
      <Route path="/tenant" element={
        <ProtectedRoute allowedRoles={["tenant"]}>
          <TenantDashboard />
        </ProtectedRoute>
      } />
      <Route path="/tenant/report" element={
        <ProtectedRoute allowedRoles={["tenant"]}>
          <ReportIssuePage />
        </ProtectedRoute>
      } />
      <Route path="/tenant/issue/:issueId/chat" element={
        <ProtectedRoute allowedRoles={["tenant"]}>
          <IssueChatPage />
        </ProtectedRoute>
      } />
      <Route path="/tenant/job/:jobId" element={
        <ProtectedRoute allowedRoles={["tenant"]}>
          <JobTrackingPage />
        </ProtectedRoute>
      } />

      {/* Contractor routes */}
      <Route path="/contractor" element={
        <ProtectedRoute allowedRoles={["contractor"]}>
          <ContractorDashboard />
        </ProtectedRoute>
      } />
      <Route path="/contractor/job/:jobId" element={
        <ProtectedRoute allowedRoles={["contractor"]}>
          <ContractorJobDetailPage />
        </ProtectedRoute>
      } />
      <Route path="/contractor/availability" element={
        <ProtectedRoute allowedRoles={["contractor"]}>
          <ContractorAvailability />
        </ProtectedRoute>
      } />
      <Route path="/contractor/invoices" element={
        <ProtectedRoute allowedRoles={["contractor"]}>
          <ContractorInvoicesPage />
        </ProtectedRoute>
      } />
      <Route path="/contractor/messages" element={
        <ProtectedRoute allowedRoles={["contractor"]}>
          <ContractorMessagesPage />
        </ProtectedRoute>
      } />
      <Route path="/contractor/settings" element={
        <ProtectedRoute allowedRoles={["contractor"]}>
          <ContractorSettingsPage />
        </ProtectedRoute>
      } />
      {/* Public contractor onboarding */}
      <Route path="/contractor/onboard" element={<ContractorOnboardPage />} />

      {/* Public contract acceptance */}
      <Route path="/contract/accept/:token" element={<ContractAcceptPage />} />

      {/* Landlord routes */}
      <Route path="/landlord" element={
        <ProtectedRoute allowedRoles={["landlord"]}>
          <LandlordDashboard />
        </ProtectedRoute>
      } />
      <Route path="/landlord/job/:jobId" element={
        <ProtectedRoute allowedRoles={["landlord"]}>
          <JobDetailPage />
        </ProtectedRoute>
      } />

      {/* My Documents - AML uploads for clients */}
      <Route path="/my-documents" element={
        <ProtectedRoute allowedRoles={["tenant", "landlord", "buyer", "seller"]}>
          <MyDocumentsPage />
        </ProtectedRoute>
      } />

      {/* Public document signing route */}
      <Route path="/sign/:token" element={<DocumentSignPage />} />
      
      {/* Public inspection booking route */}
      <Route path="/book-inspection/:token" element={<BookInspectionPage />} />

      {/* Public landlord demo page */}
      <Route path="/landlord-demo" element={<LandlordDemoPage />} />

      {/* Public seller brochure approval page */}
      <Route path="/seller/brochure-approval/:token" element={<SellerBrochureApprovalPage />} />

      {/* Inventory Clerk routes */}
      <Route path="/inventory" element={
        <ProtectedRoute allowedRoles={["admin", "director", "inventory_clerk", "lettings_manager", "lettings_negotiator", "senior_lettings_negotiator"]}>
          <InventoryDashboard />
        </ProtectedRoute>
      } />

      {/* Inventory Reports */}
      <Route path="/inventory/reports" element={
        <ProtectedRoute allowedRoles={["admin", "director", "inventory_clerk", "lettings_manager", "lettings_negotiator", "senior_lettings_negotiator"]}>
          <InventoryReportsPage />
        </ProtectedRoute>
      } />
      <Route path="/inventory/report/:reportId" element={
        <ProtectedRoute allowedRoles={["admin", "director", "inventory_clerk", "lettings_manager", "lettings_negotiator", "senior_lettings_negotiator"]}>
          <ReportEditorPage />
        </ProtectedRoute>
      } />
      <Route path="/inventory/report/:reportId/comparison" element={
        <ProtectedRoute allowedRoles={["admin", "director", "inventory_clerk", "lettings_manager", "lettings_negotiator", "senior_lettings_negotiator"]}>
          <ReportComparisonPage />
        </ProtectedRoute>
      } />
      
      {/* Public tenant report view (no auth required) */}
      <Route path="/inventory/tenant/:token" element={<TenantReportViewPage />} />
      
      {/* Public landlord report view (no auth required) */}
      <Route path="/inventory/landlord/:token" element={<LandlordReportViewPage />} />

      {/* Home Dashboard - Module Launcher for staff */}
      <Route path="/dashboard" element={
        <ProtectedRoute allowedRoles={STAFF_ROLES}>
          <ModuleLauncher />
        </ProtectedRoute>
      } />
      <Route path="/home" element={
        <ProtectedRoute allowedRoles={STAFF_ROLES}>
          <ModuleLauncher />
        </ProtectedRoute>
      } />
      <Route path="/my-dashboard" element={
        <ProtectedRoute allowedRoles={STAFF_ROLES}>
          <HomeDashboard />
        </ProtectedRoute>
      } />
      <Route path="/diary" element={
        <ProtectedRoute allowedRoles={STAFF_ROLES}>
          <ModuleGuard module={MODULES.MAINTENANCE}>
            <DiaryPage />
          </ModuleGuard>
        </ProtectedRoute>
      } />

      {/* Maintenance Module Routes */}
      <Route path="/maintenance" element={
        <ProtectedRoute allowedRoles={STAFF_ROLES}>
          <ModuleGuard module={MODULES.MAINTENANCE}>
            <MaintenanceDashboard />
          </ModuleGuard>
        </ProtectedRoute>
      } />
      <Route path="/maintenance/job/:jobId" element={
        <ProtectedRoute allowedRoles={STAFF_ROLES}>
          <ModuleGuard module={MODULES.MAINTENANCE}>
            <JobDetailPage />
          </ModuleGuard>
        </ProtectedRoute>
      } />

      {/* Admin routes */}
      <Route path="/admin" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <AdminDashboard />
        </ProtectedRoute>
      } />
      
      {/* Staff & Commission Routes */}
      <Route path="/commission" element={
        <ProtectedRoute allowedRoles={["admin", "director", "sales_negotiator", "lettings_negotiator", "sales_agent", "lettings_manager"]}>
          <CommissionDashboard />
        </ProtectedRoute>
      } />
      <Route path="/staff" element={
        <ProtectedRoute allowedRoles={["admin", "director"]}>
          <StaffManagement />
        </ProtectedRoute>
      } />
      <Route path="/my-profile" element={
        <ProtectedRoute allowedRoles={["admin", "director", "sales_agent", "lettings_manager", "accounts", "sales_negotiator", "lettings_negotiator", "inventory_clerk"]}>
          <StaffProfile />
        </ProtectedRoute>
      } />
      <Route path="/staff/:userId/profile" element={
        <ProtectedRoute allowedRoles={["admin", "director"]}>
          <StaffProfile />
        </ProtectedRoute>
      } />
      
      <Route path="/admin/job/:jobId" element={
        <ProtectedRoute allowedRoles={["admin", "director", "lettings_manager", "sales_manager", "property_manager"]}>
          <JobDetailPage />
        </ProtectedRoute>
      } />
      <Route path="/maintenance/job/:jobId" element={
        <ProtectedRoute allowedRoles={["admin", "director", "lettings_manager", "sales_manager", "property_manager"]}>
          <JobDetailPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/properties" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <PropertiesPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/inspections" element={
        <ProtectedRoute allowedRoles={["admin", "director", "inventory_clerk"]}>
          <ModuleGuard module={MODULES.LETTINGS}>
            <InspectionsPage />
          </ModuleGuard>
        </ProtectedRoute>
      } />
      <Route path="/admin/inspections/new" element={
        <ProtectedRoute allowedRoles={["admin", "director", "inventory_clerk"]}>
          <ModuleGuard module={MODULES.LETTINGS}>
            <InspectionsPage />
          </ModuleGuard>
        </ProtectedRoute>
      } />
      <Route path="/admin/compliance" element={
        <ProtectedRoute allowedRoles={["admin", "director", "lettings_manager"]}>
          <ModuleGuard module={MODULES.LETTINGS}>
            <CompliancePage />
          </ModuleGuard>
        </ProtectedRoute>
      } />
      <Route path="/admin/aml-verification" element={
        <ProtectedRoute allowedRoles={["admin", "director"]}>
          <AMLVerificationPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/payments" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <PaymentTrackingPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/sync" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <SyncPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/lettings" element={
        <ProtectedRoute allowedRoles={STAFF_ROLES}>
          <ModuleGuard module={MODULES.LETTINGS}>
            <LettingsPage />
          </ModuleGuard>
        </ProtectedRoute>
      } />
      <Route path="/lettings" element={
        <ProtectedRoute allowedRoles={STAFF_ROLES}>
          <ModuleGuard module={MODULES.LETTINGS}>
            <LettingsPage />
          </ModuleGuard>
        </ProtectedRoute>
      } />
      <Route path="/admin/lettings/rent" element={
        <ProtectedRoute allowedRoles={STAFF_ROLES}>
          <ModuleGuard module={MODULES.LETTINGS}>
            <RentManagementPage />
          </ModuleGuard>
        </ProtectedRoute>
      } />
      <Route path="/admin/lettings/landlord/:landlordId" element={
        <ProtectedRoute allowedRoles={STAFF_ROLES}>
          <ModuleGuard module={MODULES.LETTINGS}>
            <LandlordDetailPage />
          </ModuleGuard>
        </ProtectedRoute>
      } />
      <Route path="/admin/lettings/tenant/:tenantId" element={
        <ProtectedRoute allowedRoles={STAFF_ROLES}>
          <ModuleGuard module={MODULES.LETTINGS}>
            <TenantDetailPage />
          </ModuleGuard>
        </ProtectedRoute>
      } />
      <Route path="/admin/lettings/property/:propertyId" element={
        <ProtectedRoute allowedRoles={STAFF_ROLES}>
          <ModuleGuard module={MODULES.LETTINGS}>
            <PropertyDetailPage />
          </ModuleGuard>
        </ProtectedRoute>
      } />
      <Route path="/admin/lettings/import" element={
        <ProtectedRoute allowedRoles={STAFF_ROLES}>
          <ModuleGuard module={MODULES.LETTINGS}>
            <BulkImportPage />
          </ModuleGuard>
        </ProtectedRoute>
      } />
      <Route path="/admin/lettings/bulk-contacts" element={
        <ProtectedRoute allowedRoles={STAFF_ROLES}>
          <ModuleGuard module={MODULES.LETTINGS}>
            <BulkContactsUpdate />
          </ModuleGuard>
        </ProtectedRoute>
      } />
      <Route path="/admin/lettings/analytics" element={
        <ProtectedRoute allowedRoles={STAFF_ROLES}>
          <ModuleGuard module={MODULES.LETTINGS}>
            <LettingsAnalyticsPage />
          </ModuleGuard>
        </ProtectedRoute>
      } />
      <Route path="/admin/maintenance/analytics" element={
        <ProtectedRoute allowedRoles={STAFF_ROLES}>
          <ModuleGuard module={MODULES.MAINTENANCE}>
            <MaintenanceAnalyticsPage />
          </ModuleGuard>
        </ProtectedRoute>
      } />
      <Route path="/admin/contractors/:contractorId" element={
        <ProtectedRoute allowedRoles={STAFF_ROLES}>
          <ContractorDetailsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/reports" element={
        <ProtectedRoute allowedRoles={STAFF_ROLES}>
          <AdminReportsPage />
        </ProtectedRoute>
      } />

      {/* Accounts routes - restricted to director, admin, accounts, accounts_admin */}
      <Route path="/accounts" element={
        <ProtectedRoute allowedRoles={["director", "admin", "accounts", "accounts_admin"]}>
          <AccountsDashboard />
        </ProtectedRoute>
      } />
      <Route path="/accounts/xero" element={
        <ProtectedRoute allowedRoles={["director", "admin", "accounts", "accounts_admin"]}>
          <XeroSyncPage />
        </ProtectedRoute>
      } />
      <Route path="/accounts/xero/callback" element={
        <XeroCallbackPage />
      } />
      <Route path="/accounts/services" element={
        <ProtectedRoute allowedRoles={["director", "admin", "accounts", "accounts_admin"]}>
          <LandlordServicesPage />
        </ProtectedRoute>
      } />
      <Route path="/accounts/invoicing" element={
        <ProtectedRoute allowedRoles={["director", "admin", "accounts", "accounts_admin"]}>
          <AccountsInvoicingPage />
        </ProtectedRoute>
      } />

      {/* Contractor inspections route */}
      <Route path="/contractor/inspections" element={
        <ProtectedRoute allowedRoles={["contractor"]}>
          <InspectionsPage />
        </ProtectedRoute>
      } />

      {/* Landlord inspections route */}
      <Route path="/landlord/inspections" element={
        <ProtectedRoute allowedRoles={["landlord"]}>
          <InspectionsPage />
        </ProtectedRoute>
      } />

      {/* Contacts routes (new workflow) */}
      <Route path="/contacts" element={
        <ProtectedRoute allowedRoles={STAFF_ROLES}>
          <ContactsPage />
        </ProtectedRoute>
      } />
      <Route path="/contacts/:contactId" element={
        <ProtectedRoute allowedRoles={STAFF_ROLES}>
          <ContactDetailPage />
        </ProtectedRoute>
      } />

      {/* Simplified New Instruction (ONE page for all) */}
      <Route path="/new-instruction" element={
        <ProtectedRoute allowedRoles={STAFF_ROLES}>
          <SimpleNewInstructionPage />
        </ProtectedRoute>
      } />

      {/* Client Dashboard (unified view) */}
      <Route path="/client/:clientId" element={
        <ProtectedRoute allowedRoles={STAFF_ROLES}>
          <ClientDashboardPage />
        </ProtectedRoute>
      } />

      {/* Listings routes (new workflow) */}
      <Route path="/listings/new" element={
        <ProtectedRoute allowedRoles={STAFF_ROLES}>
          <ListingNewPage />
        </ProtectedRoute>
      } />
      <Route path="/listings/:listingId" element={
        <ProtectedRoute allowedRoles={STAFF_ROLES}>
          <NewListingDetailPage />
        </ProtectedRoute>
      } />

      {/* Progression routes (Stage 3) */}
      <Route path="/progression/sales/:caseId" element={
        <ProtectedRoute allowedRoles={STAFF_ROLES}>
          <SalesProgressionPage />
        </ProtectedRoute>
      } />
      <Route path="/progression/tenancy/:tenancyId" element={
        <ProtectedRoute allowedRoles={STAFF_ROLES}>
          <TenancyProgressionPage />
        </ProtectedRoute>
      } />

      {/* Sales routes */}
      <Route path="/sales" element={
        <ProtectedRoute allowedRoles={[...STAFF_ROLES, "seller_solicitor", "buyer_solicitor", "broker", "seller", "buyer", "solicitor"]}>
          <SalesDashboard />
        </ProtectedRoute>
      } />
      <Route path="/sales/simple" element={
        <ProtectedRoute allowedRoles={STAFF_ROLES}>
          <SimpleFlowsPage />
        </ProtectedRoute>
      } />
      <Route path="/sales/new-instruction" element={
        <ProtectedRoute allowedRoles={STAFF_ROLES}>
          <NewInstructionPage />
        </ProtectedRoute>
      } />
      <Route path="/sales/landing" element={
        <ProtectedRoute allowedRoles={[...STAFF_ROLES, "seller_solicitor", "buyer_solicitor", "broker", "seller", "buyer", "solicitor"]}>
          <SalesLandingPage />
        </ProtectedRoute>
      } />
      <Route path="/sales/properties" element={
        <ProtectedRoute allowedRoles={[...STAFF_ROLES, "seller_solicitor", "buyer_solicitor", "broker", "seller", "buyer", "solicitor"]}>
          <InstructionsListPage />
        </ProtectedRoute>
      } />
      <Route path="/sales/progressor" element={
        <ProtectedRoute allowedRoles={STAFF_ROLES}>
          <SalesProgressorDashboard />
        </ProtectedRoute>
      } />
      {/* Clean Sales Progression Dashboard for all sales staff */}
      <Route path="/progression" element={
        <ProtectedRoute allowedRoles={["sales_negotiator", "sales_agent", "sales_progressor", "admin", "director"]}>
          <TraceyDashboard />
        </ProtectedRoute>
      } />
      <Route path="/sales/solicitors" element={
        <ProtectedRoute allowedRoles={[...STAFF_ROLES, "solicitor"]}>
          <SolicitorFirmsPage />
        </ProtectedRoute>
      } />
      <Route path="/sales/solicitor/:firmId" element={
        <ProtectedRoute allowedRoles={[...STAFF_ROLES, "solicitor"]}>
          <SolicitorFirmDashboard />
        </ProtectedRoute>
      } />
      <Route path="/sales/solicitor/:firmId/analytics" element={
        <ProtectedRoute allowedRoles={[...STAFF_ROLES, "solicitor"]}>
          <SolicitorAnalyticsPage />
        </ProtectedRoute>
      } />
      <Route path="/solicitor/templates" element={
        <ProtectedRoute allowedRoles={[...STAFF_ROLES, "solicitor", "seller_solicitor", "buyer_solicitor"]}>
          <SolicitorTemplatesPage />
        </ProtectedRoute>
      } />
      <Route path="/sales/pipeline" element={
        <ProtectedRoute allowedRoles={[...STAFF_ROLES, "seller_solicitor", "buyer_solicitor", "broker", "seller", "buyer", "solicitor"]}>
          <SalesDashboard initialTab="progression" />
        </ProtectedRoute>
      } />
      <Route path="/sales/chain" element={
        <ProtectedRoute allowedRoles={STAFF_ROLES}>
          <SalesChainPage />
        </ProtectedRoute>
      } />
      <Route path="/sales/offers" element={
        <ProtectedRoute allowedRoles={[...STAFF_ROLES, "seller_solicitor", "buyer_solicitor", "broker", "seller", "buyer", "solicitor"]}>
          <InstructionsListPage />
        </ProtectedRoute>
      } />
      <Route path="/sales/completed" element={
        <ProtectedRoute allowedRoles={[...STAFF_ROLES, "seller_solicitor", "buyer_solicitor", "broker", "seller", "buyer", "solicitor"]}>
          <SalesDashboard initialTab="completed" />
        </ProtectedRoute>
      } />
      <Route path="/sales/instructions" element={
        <ProtectedRoute allowedRoles={[...STAFF_ROLES, "seller_solicitor", "buyer_solicitor", "broker", "seller", "buyer", "solicitor"]}>
          <InstructionsListPage />
        </ProtectedRoute>
      } />
      
      {/* Sales Instructions Module (ISOLATED) */}
      <Route path="/sales/instructions/list" element={
        <ProtectedRoute allowedRoles={STAFF_ROLES}>
          <SIInstructionsListPage />
        </ProtectedRoute>
      } />
      <Route path="/sales/instructions/:instructionId" element={
        <ProtectedRoute allowedRoles={STAFF_ROLES}>
          <SIPropertyOverviewPage />
        </ProtectedRoute>
      } />
      
      {/* Portal Publishing Admin */}
      <Route path="/admin/publishing-jobs" element={
        <ProtectedRoute allowedRoles={["admin", "director"]}>
          <PublishingJobsPage />
        </ProtectedRoute>
      } />
      
      {/* SI-P0-04: Redirect old /sales/listings to new SI module */}
      <Route path="/sales/listings" element={<Navigate to="/sales/instructions/list" replace />} />
      
      <Route path="/sales/instruction-detail/:instructionId" element={
        <ProtectedRoute allowedRoles={STAFF_ROLES}>
          <InstructionDetailPage />
        </ProtectedRoute>
      } />
      <Route path="/sales/property-hub/:instructionId" element={
        <ProtectedRoute allowedRoles={STAFF_ROLES}>
          <InstructionHubPage />
        </ProtectedRoute>
      } />
      <Route path="/sales/new" element={
        <ProtectedRoute allowedRoles={STAFF_ROLES}>
          <SalesDashboard initialTab="new" />
        </ProtectedRoute>
      } />
      <Route path="/sales/listing/:listingId" element={
        <ProtectedRoute allowedRoles={STAFF_ROLES}>
          <ListingDetailPage />
        </ProtectedRoute>
      } />
      <Route path="/sales/property/:caseId" element={
        <ProtectedRoute allowedRoles={[...STAFF_ROLES, "seller", "buyer", "seller_solicitor", "buyer_solicitor", "broker", "solicitor"]}>
          <SalesPropertyPage />
        </ProtectedRoute>
      } />
      <Route path="/sales/contracts" element={
        <ProtectedRoute allowedRoles={STAFF_ROLES}>
          <ContractsManagementPage />
        </ProtectedRoute>
      } />
      <Route path="/sales/instruction/:caseId" element={
        <ProtectedRoute allowedRoles={[...STAFF_ROLES, "seller", "buyer", "seller_solicitor", "buyer_solicitor", "broker", "solicitor"]}>
          <SalesPropertyPage />
        </ProtectedRoute>
      } />
      <Route path="/sales/case/:caseId" element={
        <ProtectedRoute allowedRoles={[...STAFF_ROLES, "seller", "buyer", "seller_solicitor", "buyer_solicitor", "broker", "solicitor"]}>
          <CaseHubPage />
        </ProtectedRoute>
      } />
      <Route path="/sales/case/:caseId/hub" element={
        <ProtectedRoute allowedRoles={[...STAFF_ROLES, "seller", "buyer", "seller_solicitor", "buyer_solicitor", "broker", "solicitor"]}>
          <CaseHubPage />
        </ProtectedRoute>
      } />
      <Route path="/sales/case/:caseId/progression" element={
        <ProtectedRoute allowedRoles={[...STAFF_ROLES, "seller", "buyer", "seller_solicitor", "buyer_solicitor", "broker", "solicitor"]}>
          <CaseHubPage />
        </ProtectedRoute>
      } />
      <Route path="/sales/case/:caseId/chain" element={
        <ProtectedRoute allowedRoles={STAFF_ROLES}>
          <ChainBuilderPage />
        </ProtectedRoute>
      } />
      <Route path="/sales/import" element={
        <ProtectedRoute allowedRoles={STAFF_ROLES}>
          <SalesImportPage />
        </ProtectedRoute>
      } />
      <Route path="/sales/analytics" element={
        <ProtectedRoute allowedRoles={STAFF_ROLES}>
          <SalesAnalyticsPage />
        </ProtectedRoute>
      } />
      <Route path="/sales/applicant/:applicantId" element={
        <ProtectedRoute allowedRoles={STAFF_ROLES}>
          <ApplicantDetailsPage />
        </ProtectedRoute>
      } />
      <Route path="/sales/vendor/:vendorId" element={
        <ProtectedRoute allowedRoles={STAFF_ROLES}>
          <VendorDetailPage />
        </ProtectedRoute>
      } />
      <Route path="/sales/buyer/:buyerId" element={
        <ProtectedRoute allowedRoles={STAFF_ROLES}>
          <BuyerDetailPage />
        </ProtectedRoute>
      } />
      <Route path="/sales/case/:caseId/documents" element={
        <ProtectedRoute allowedRoles={STAFF_ROLES}>
          <CaseDocumentsPage />
        </ProtectedRoute>
      } />
      <Route path="/sales/case/:caseId/marketing" element={
        <ProtectedRoute allowedRoles={STAFF_ROLES}>
          <MarketingDetailsPage />
        </ProtectedRoute>
      } />
      <Route path="/sales/case/:caseId/particulars" element={
        <ProtectedRoute allowedRoles={STAFF_ROLES}>
          <SalesParticularsPage />
        </ProtectedRoute>
      } />
      <Route path="/sales/case/:caseId/brochure" element={
        <ProtectedRoute allowedRoles={["director", "admin", "sales_agent", "accounts", "lettings_manager", "sales_negotiator"]}>
          <PropertyBrochurePage />
        </ProtectedRoute>
      } />
      <Route path="/sales/case/:caseId/offers" element={
        <ProtectedRoute allowedRoles={["director", "admin", "sales_agent", "accounts", "lettings_manager", "sales_negotiator"]}>
          <CaseOffersPage />
        </ProtectedRoute>
      } />
      <Route path="/sales/case/:caseId/viewings" element={
        <ProtectedRoute allowedRoles={["director", "admin", "sales_agent", "accounts", "lettings_manager", "sales_negotiator"]}>
          <CaseViewingsPage />
        </ProtectedRoute>
      } />

      {/* Marketing - Portal Exports */}
      <Route path="/marketing/portals" element={
        <ProtectedRoute allowedRoles={["director", "admin", "sales_agent", "lettings_manager"]}>
          <PortalExportsPage />
        </ProtectedRoute>
      } />
      <Route path="/marketing/properties" element={
        <ProtectedRoute allowedRoles={["director", "admin", "sales_agent", "lettings_manager"]}>
          <PropertyMarketingPage />
        </ProtectedRoute>
      } />

      {/* Solicitor Dashboard */}
      <Route path="/solicitor" element={
        <ProtectedRoute allowedRoles={["seller_solicitor", "buyer_solicitor"]}>
          <SolicitorDashboard />
        </ProtectedRoute>
      } />

      {/* Client Portal Routes - No auth wrapper, portal handles its own auth */}
      <Route path="/portal/login" element={<PortalLogin />} />
      <Route path="/portal/register" element={<PortalRegister />} />
      <Route path="/portal/seller" element={<SellerDashboard />} />
      <Route path="/portal/seller/contract" element={<SellerContractPage />} />
      <Route path="/portal/seller/progression/:caseId" element={<SellerProgressionPage />} />
      <Route path="/portal/seller/particulars/:caseId" element={<SellerParticularsReview />} />
      
      {/* Seller Portal - Sales Instructions (ISOLATED) */}
      <Route path="/portal/seller/instruction" element={<SellerInstructionPage />} />
      
      <Route path="/portal/buyer" element={<BuyerDashboardPortal />} />
      <Route path="/portal/landlord" element={<PortalLandlordDashboard />} />
      <Route path="/portal/tenant" element={<PortalTenantDashboard />} />

      {/* Solicitor Portal Routes */}
      <Route path="/solicitor-portal/login" element={<SolicitorPortalLogin />} />
      <Route path="/solicitor-portal" element={<SolicitorPortalDashboard />} />
      <Route path="/solicitor-portal/case/:caseId" element={<SolicitorPortalDashboard />} />

      {/* Buyer Portal Routes - Self-registration & property search */}
      <Route path="/apply" element={<ApplicantRegister />} />
      <Route path="/buyer/login" element={<BuyerLogin />} />
      <Route path="/buyer/dashboard" element={<BuyerDashboard />} />
      <Route path="/buyer/properties" element={<PropertySearch />} />
      <Route path="/buyer/viewings" element={<BuyerViewings />} />
      <Route path="/buyer/onboarding" element={<BuyerOnboarding />} />

      {/* Default redirect */}
      <Route path="/" element={<DefaultRedirect />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// Default redirect based on user role
function DefaultRedirect() {
  const user = JSON.parse(localStorage.getItem("user") || "null");
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  const dashboardRoutes = {
    tenant: "/tenant",
    contractor: "/contractor",
    landlord: "/landlord",
    admin: "/dashboard",
    seller_solicitor: "/solicitor",
    buyer_solicitor: "/solicitor",
    broker: "/sales",
    seller: "/portal/seller",
    buyer: "/portal/buyer",
    director: "/dashboard",  // Director sees home dashboard
    sales_agent: "/dashboard",  // Sales + Lettings view, no accounts
    accounts: "/accounts",  // Accounts team - financial data, lettings, sales & Xero
    inventory_clerk: "/inventory",  // Stacy - manages inspections + contractors
    lettings_manager: "/dashboard",  // Lettings + Sales, no accounts
  };

  return <Navigate to={dashboardRoutes[user.role] || "/login"} replace />;
}

function App() {
  const isLoggedIn = !!localStorage.getItem("token");
  const userStr = localStorage.getItem("user");
  const user = userStr ? JSON.parse(userStr) : null;
  
  // Check if user is on solicitor portal (uses separate auth)
  const isSolicitorPortal = window.location.pathname.startsWith("/solicitor-portal");
  const hasSolicitorToken = !!localStorage.getItem("solicitor_token");
  
  // Don't show nav for portal users (sellers, buyers, landlords, tenants)
  // Also hide for sales_negotiator who have their own clean progression dashboard
  // Also hide for solicitor portal users
  const isPortalUser = user?.client_type || ["tenant", "contractor", "landlord", "buyer", "seller"].includes(user?.role);
  const isSalesNegotiator = user?.role === "sales_negotiator";
  const showRoleSwitcher = isLoggedIn && !isSolicitorPortal;
  
  return (
    <ErrorBoundary>
      <div className="App min-h-screen bg-background">
        <BrowserRouter>
          <AppContent 
            isLoggedIn={isLoggedIn}
            isPortalUser={isPortalUser}
            isSalesNegotiator={isSalesNegotiator}
            isSolicitorPortal={isSolicitorPortal}
            showRoleSwitcher={showRoleSwitcher}
          />
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </div>
    </ErrorBoundary>
  );
}

// Wrapper component to use useLocation for nav visibility
function AppContent({ isLoggedIn, isPortalUser, isSalesNegotiator, isSolicitorPortal, showRoleSwitcher }) {
  const location = useLocation();
  
  // Check if on module launcher (no nav needed on launcher)
  const isOnLauncher = location.pathname === "/home" || location.pathname === "/dashboard";
  
  // Show nav for staff, but not on launcher
  const showGlobalNav = isLoggedIn && !isPortalUser && !isSalesNegotiator && !isSolicitorPortal && !isOnLauncher;
  
  return (
    <>
      {showGlobalNav && <ModuleNav />}
      <AppRouter />
      {showRoleSwitcher && <RoleSwitcher />}
    </>
  );
}

export default App;
